// client/src/utils/demoMode.ts (converted from .js, ST-00230)
//
// Demo mode: LexiBridge shown to a prospect on real data, with every customer name,
// registration, reference number, contact detail and free-text note replaced by a made-up one.
// Who may switch it on is decided in context/DemoModeContext (unchanged).
//
// PLT-12 (ST-00230) rebuilt the masking to fail closed:
//   - Field names are normalised before the lookup (lower case, no spaces, underscores or
//     punctuation), so `dispatch_location`, `Dispatch Location`, `dispatchLocation` and
//     `DispatchLocation` are one field. pg answers in snake_case, which the old map (display,
//     camel and Pascal names only) let through on Transaction Allocation and Route Intelligence.
//   - Exact names plus suffix rules (`…_location_name`, `…_reg_no`, `…ed_by`, `…_email`, …), and
//     a generic `name` / `label` takes the meaning of what holds it (`drivers[].name`
//     is a person, `locations[].name` a place; a bare list of names takes it from the URL).
//   - Every axios client installs it (installDemoMasking): the shared `api`, apiService (Order
//     Management) and the weighbridge-compliance client. Order Management's socket payloads go
//     through maskResponseData too (hooks/useOrderSubscription).
//   - Only plain objects, arrays and strings are walked: a Blob / ArrayBuffer download is
//     handed over untouched (it used to become `{}`).
//   - It never masks twice: masked containers are remembered, and a masked value is never
//     masked again (a response retried after a 401 refresh used to get new names).
//   - Each mask stands for one real value (a clash gets a numbered suffix), so requests carry
//     the real value back: a drill-down by a masked order number, a filter by a masked site, or
//     a form saved with masked fields reaches the server with the real values
//     (unmaskRequestConfig). Without that, the wider masking would break drill-downs and write
//     made-up values into real records.
//
// Not covered: free text under generic keys (`description`, `error` messages), map
// coordinates, and files the server builds (exports are passed through untouched).

import type { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

let _isDemoMode = false;

export const DEMO_ALLOWED_EMAILS: readonly string[] = [
  'admin@lex-adapt.com',
  'bryan@lex-adapt.com',
  'johan@lex-adapt.com',
];

export const isDemoMode = (): boolean => _isDemoMode;
export const setDemoMode = (value: unknown): void => {
  _isDemoMode = !!value;
};

/** What a masked value stands for, which decides what it is replaced with. */
export type MaskCategory =
  | 'location'
  | 'company'
  | 'route'
  | 'person'
  | 'ticketId'
  | 'code'
  | 'vehicle'
  | 'product'
  | 'email'
  | 'phone'
  | 'address'
  | 'city'
  | 'text';

// ── Field names ─────────────────────────────────────────────────────────────

/** `Dispatch Location`, `dispatch_location`, `dispatchLocation` → `dispatchlocation`. */
export const normaliseKey = (key: string): string => key.toLowerCase().replace(/[^a-z0-9]/g, '');

const FIELD_CATEGORY = new Map<string, MaskCategory>();
const register = (category: MaskCategory, keys: readonly string[]): void => {
  for (const key of keys) FIELD_CATEGORY.set(normaliseKey(key), category);
};

register('location', [
  'Dispatch Location',
  'Receipt Location',
  // The source columns: the organisation at the dispatch / receipt site (a site, as before).
  'Dispatch Organisation',
  'Receipt Organisation',
  'Dispatch Client',
  'Receipt Client',
  'location',
  'locations',
  'primary_location',
  'related_location',
  'dispatch_locations',
  'receipt_locations',
  'site',
  'site_name',
  'from_name',
  'to_name',
  'source_name',
  'destination_name',
  'origin',
  'destination',
  'geofence_name',
  'zone_name',
  'depot',
  'depot_name',
  'unmapped',
]);

register('company', [
  'haulier',
  'hauliers',
  'hauler',
  'haulers',
  'transporter',
  'transporters',
  'customer',
  'customers',
  'company',
  'companies',
  'supplier',
  'suppliers',
  'client_name',
  'tenant_name',
  'organisation',
  'organization',
  'organisation_name',
  'consignee',
  'consignor',
]);

register('route', ['grouping', 'grouping_name', 'route_name', 'route_grouping']);

register('person', [
  'driver',
  'drivers',
  'driver_name',
  'signer_name',
  'signed_by',
  'signed_by_name',
  'submitted_by',
  'reviewed_by',
  'amendment_user',
  'username',
  'user_name',
  'first_name',
  'last_name',
  'full_name',
  'display_name',
  'contact_name',
  'contact_person',
  'primary_contact_name',
  'event_user',
  'operator_name',
]);

register('ticketId', [
  'ticket_uid',
  'dispatch_uid',
  'receipt_uid',
  'matched_ticket_uid',
  'reference',
  'reference_number',
  'ref_no',
  'order_ref',
  'trip_reference',
  'ticket_ref',
  'order_external_reference',
  'po_number',
  'po_no',
  'invoice_no',
  'invoice_number',
  'waybill_no',
  'consignment_no',
]);

register('code', [
  'company_code',
  'location_code',
  'product_code',
  'hauler_code',
  'haulier_code',
  'license_number',
  'licence_number',
  'tax_identification',
  'tax_number',
  'vat_number',
  'business_registration',
  'postal_code',
  'driver_tag',
  'asset_name',
  'asset_key',
]);

register('vehicle', [
  'vehicle',
  'vehicles',
  'reg_no',
  'vehicle_reg',
  'vehicle_reg_no',
  'vehicle_registration',
  'registration',
  'registration_number',
  'registration_normalised',
  'plate_number',
  'fleet_no',
  'fleet_number',
  'trailer_ids',
]);

register('product', [
  'product',
  'products',
  'product_ref',
  'Product Ref.',
  'product_name',
  'product_type',
  'product_description',
  'material',
]);

register('email', [
  'email',
  'emails',
  'email_address',
  'email_addresses',
  'recipients',
  'email_recipients',
  'to',
  'cc',
  'bcc',
]);

register('phone', ['phone', 'phone_e164', 'contact_number', 'mobile', 'cell', 'telephone']);

register('address', [
  'address',
  'street_address',
  'address_line_1',
  'address_line_2',
  'physical_address',
  'postal_address',
]);

register('city', ['city', 'town', 'suburb']);

register('text', [
  'notes',
  'note',
  'line_notes',
  'comment',
  'comments',
  'remarks',
  'amendment_reason',
  'event_description',
  'change_description',
]);

/** Suffix rules, for the spellings the exact list does not know (checked in order). */
const SUFFIX_RULES: ReadonlyArray<readonly [RegExp, MaskCategory]> = [
  [/^trailerreg\d*$/, 'vehicle'],
  [/(regno|vehiclereg|platenumber|registrationnumber|fleetno)$/, 'vehicle'],
  [
    /(ticketuid|ticketno|ticketnumber|orderno|ordernumber|deliveryno|deliverynumber|refno|referenceno|referencenumber)$/,
    'ticketId',
  ],
  [/^(?!.*allocations?$).*(locationname|locations?|sitename)$/, 'location'],
  [
    /(companyname|customername|hauliername|haulername|transportername|suppliername|organisationname|organizationname)$/,
    'company',
  ],
  [/(groupingname|routename)$/, 'route'],
  [/(drivername|contactname|username|fullname|firstname|lastname|displayname)$/, 'person'],
  // `created_by`, `approved_by`, `signed_off_by`, … (not `sort_by` / `group_by` / `order_by`).
  [/^(?!sort|group|order|partition)[a-z]*(ed|off)by$/, 'person'],
  [/emails?$/, 'email'],
  [/(phone|phonenumber|contactnumber|cellnumber|mobilenumber)$/, 'phone'],
  [/productname$/, 'product'],
];

/** The category of a normalised field name, if it holds something to mask. */
export function categoryOfKey(normalisedKey: string): MaskCategory | null {
  const exact = FIELD_CATEGORY.get(normalisedKey);
  if (exact) return exact;
  for (const [pattern, category] of SUFFIX_RULES) {
    if (pattern.test(normalisedKey)) return category;
  }
  return null;
}

/** Fields that name "the thing that holds them": `drivers[].name`, `[{ value, label }]`. */
const GENERIC_KEYS = new Set(['name', 'label']);

/** Container words that say what the objects under them are. */
const CONTEXT_WORDS = new Map<string, MaskCategory>();
const context = (category: MaskCategory, words: readonly string[]): void => {
  for (const word of words) CONTEXT_WORDS.set(word, category);
};
context('person', [
  'users',
  'user',
  'signers',
  'signer',
  'operators',
  'operator',
  'contacts',
  'contact',
  'members',
  'assignees',
  'approvers',
  'people',
]);
context('location', [
  'sites',
  'lanes',
  'lane',
  'zones',
  'zone',
  'geofences',
  'geofence',
  'origins',
  'destinations',
  'stops',
  'stop',
  'depots',
  'places',
  'weighbridges',
]);
context('company', ['tenants', 'tenant', 'organisations', 'organizations', 'clients']);
context('product', ['materials']);
context('vehicle', ['trucks', 'truck', 'trailers', 'trailer']);
context('code', ['assets', 'asset']);
context('route', ['routes', 'route', 'groupings', 'routegroupings', 'otherroutes']);

/** Wrappers that pass their holder's meaning on (`{ success, data: [...] }`). */
const NEUTRAL_WRAPPERS = new Set([
  'data',
  'items',
  'rows',
  'results',
  'result',
  'records',
  'list',
  'payload',
  'options',
]);

/**
 * Container words whose plain string lists are lists of names (`sites: ['…']`). Other words
 * only give `name` / `label` their meaning: `routes: ['/route-summary']` are paths.
 */
const NAME_LIST_WORDS = new Set([
  'users',
  'signers',
  'operators',
  'contacts',
  'members',
  'assignees',
  'approvers',
  'people',
  'sites',
  'lanes',
  'zones',
  'geofences',
  'origins',
  'destinations',
  'stops',
  'depots',
  'places',
  'weighbridges',
  'tenants',
  'organisations',
  'organizations',
  'materials',
  'trucks',
  'trailers',
  'assets',
  'groupings',
  'routegroupings',
]);

/** What a container stands for: for its generic fields, and for the strings of a list. */
interface MaskContext {
  generic: MaskCategory | null;
  items: MaskCategory | null;
}
const NO_CONTEXT: MaskContext = { generic: null, items: null };

/** The context of the container under `key` (a neutral wrapper passes its holder's on). */
function contextOf(key: string, holder: MaskContext): MaskContext {
  const k = normaliseKey(key);
  if (NEUTRAL_WRAPPERS.has(k)) return holder;
  const own = categoryOfKey(k);
  const word = CONTEXT_WORDS.get(k) ?? null;
  return { generic: own ?? word, items: own ?? (NAME_LIST_WORDS.has(k) ? word : null) };
}

/** Trailing path words that only narrow a list down (`/data/locations/dispatch`). */
const URL_QUALIFIERS = new Set([
  'dispatch',
  'receipt',
  'all',
  'list',
  'search',
  'active',
  'options',
  'names',
  'distinct',
]);
const ID_SEGMENT = /^(\d+|[0-9a-f]{8}-[0-9a-f-]{27,})$/i;

/**
 * The meaning of a response from its URL: what its last resource word names, after ids and
 * qualifiers (`/lexistar/drivers` → people, `/data/locations/dispatch` → sites,
 * `/companies/12/locations` → sites). Only that word counts: `/users/me/permissions` is about
 * permissions, not people.
 */
export function contextOfUrl(url: string | undefined): MaskContext {
  if (!url) return NO_CONTEXT;
  const path = url.split(/[?#]/, 1)[0] ?? '';
  const segments = path.split('/').filter(Boolean).reverse();
  for (const segment of segments) {
    if (ID_SEGMENT.test(segment) || URL_QUALIFIERS.has(segment.toLowerCase())) continue;
    return contextOf(segment, NO_CONTEXT);
  }
  return NO_CONTEXT;
}

// ── Made-up values ──────────────────────────────────────────────────────────

const PLACE_NAMES = [
  'Northpoint',
  'Riverside',
  'Summit',
  'Lakeside',
  'Crestview',
  'Bayport',
  'Ridgewood',
  'Clearwater',
  'Meadowbrook',
  'Stonehill',
  'Irongate',
  'Pinecrest',
  'Ashford',
  'Brookfield',
  'Cedarwood',
  'Dovedale',
  'Elmhurst',
  'Foxridge',
  'Glenmore',
  'Hawthorne',
  'Ivydale',
  'Juniper',
  'Kingsley',
  'Larkfield',
  'Millbrook',
  'Oakwood',
  'Parklands',
  'Queensbury',
  'Rosewood',
  'Thornhill',
];
const PLACE_KINDS = ['Depot', 'Terminal', 'Yard', 'Hub', 'Siding', 'Wharf', 'Plant', 'Quarry'];
const COMPANY_NAMES = [
  'Acme',
  'Pinnacle',
  'Summit',
  'Atlas',
  'Beacon',
  'Compass',
  'Delta',
  'Eagle',
  'Frontier',
  'Globe',
  'Harbour',
  'Ironside',
  'Keystone',
  'Landmark',
  'Meridian',
  'Noble',
  'Olympus',
  'Pacific',
  'Quantum',
  'Redstone',
];
const COMPANY_KINDS = [
  'Logistics',
  'Transport',
  'Haulage',
  'Freight',
  'Carriers',
  'Shipping',
  'Holdings',
  'Mining',
];
const INITIALS = 'ABCDEFGHJKLMNPRSTVW'.split('');
const SURNAMES = [
  'Smith',
  'Johnson',
  'Williams',
  'Brown',
  'Davis',
  'Miller',
  'Wilson',
  'Moore',
  'Taylor',
  'Anderson',
  'Thomas',
  'Jackson',
  'White',
  'Harris',
  'Martin',
  'Clark',
  'Lewis',
  'Walker',
  'Hall',
  'Young',
];
const PRODUCT_KINDS = ['Material', 'Product', 'Commodity', 'Resource', 'Grade', 'Blend'];
const STREETS = [
  'Acacia Road',
  'Main Street',
  'Station Road',
  'Church Street',
  'Mill Lane',
  'Park Avenue',
  'Hill Street',
  'River Road',
  'Market Street',
  'Oak Avenue',
  'Quarry Road',
  'Rail Street',
];
const CITIES = [
  'Westbury',
  'Eastfield',
  'Northam',
  'Southgate',
  'Fairview',
  'Greenvale',
  'Highveld',
  'Lowmoor',
  'Redcliff',
  'Silverton',
  'Brightwater',
  'Kingsport',
];
const PROVINCES = ['GP', 'MP', 'LP', 'NW', 'KZN', 'EC', 'WC', 'FS', 'NC'];
const LETTERS = 'BCDFGHJKLMNPRSTVWXYZ'.split('');

/** djb2 with a seed, so different parts of one mask vary independently. */
function hash(str: string, seed = 5381): number {
  let h = seed;
  for (let i = 0; i < str.length; i++) {
    h = (h << 5) + h + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}
const pick = <T>(list: readonly T[], n: number): T => list[n % list.length]!;
const base36 = (n: number, length: number): string =>
  n.toString(36).toUpperCase().padStart(length, '0').slice(-length);
const digits = (n: number, length: number): string =>
  String(n % 10 ** length).padStart(length, '0');

/** A made-up value of the category, derived from the real one (before the uniqueness check). */
function candidateFor(category: Exclude<MaskCategory, 'email'>, value: string): string {
  const a = hash(value);
  const b = hash(value, 7919);
  switch (category) {
    case 'location':
      return `${pick(PLACE_NAMES, a)} ${pick(PLACE_KINDS, b)}`;
    case 'route': {
      const from = pick(PLACE_NAMES, a);
      let to = pick(PLACE_NAMES, b);
      if (to === from) to = pick(PLACE_NAMES, b + 1);
      return `${from} to ${to}`;
    }
    case 'company':
      return `${pick(COMPANY_NAMES, a)} ${pick(COMPANY_KINDS, b)}`;
    case 'person':
      return `${pick(INITIALS, a)}. ${pick(SURNAMES, b)}`;
    case 'ticketId':
      return `DM-${base36(a, 8)}`;
    case 'code':
      return `DC-${base36(a, 6)}`;
    case 'vehicle': {
      const letters = [0, 1, 2].map((i) => pick(LETTERS, Math.floor(a / 20 ** i))).join('');
      return `${letters} ${digits(b, 3)} ${pick(PROVINCES, a + b)}`;
    }
    case 'product':
      return `${pick(PRODUCT_KINDS, a)} ${pick(LETTERS, b)}${b % 10}`;
    case 'phone':
      return `+27 00 ${digits(a, 3)} ${digits(b, 4)}`;
    case 'address':
      return `${(a % 299) + 1} ${pick(STREETS, b)}`;
    case 'city':
      return pick(CITIES, a);
    case 'text':
      return `Demo note ${base36(a, 4)}`;
  }
}

// One made-up value per real value, and the way back, for this demo session (resetMasks).
const maskOf = new Map<string, string>(); // `${category}:${real}` → mask
const realOf = new Map<string, string>(); // mask → real

/** The mask for `real`, unique among the masks handed out so far. */
function remember(category: string, real: string, candidate: string): string {
  const cacheKey = `${category}:${real}`;
  const known = maskOf.get(cacheKey);
  if (known !== undefined) return known;
  let mask = candidate;
  for (let n = 2; realOf.has(mask) && realOf.get(mask) !== real; n += 1) mask = `${candidate} ${n}`;
  maskOf.set(cacheKey, mask);
  realOf.set(mask, real);
  return mask;
}

const EMAIL_PATTERN = /[^\s,;<>()"']+@[^\s,;<>()"']+\.[^\s,;<>()"']+/g;

function maskEmail(address: string): string {
  if (DEMO_ALLOWED_EMAILS.includes(address.toLowerCase())) return address;
  return remember(
    'email',
    address,
    `user.${base36(hash(address.toLowerCase()), 6).toLowerCase()}@demo.example.com`
  );
}

/** A string replaced by its mask (or as it was: empty, already a mask, nothing to mask). */
function maskString(value: string, category: MaskCategory | null): string {
  if (!category || !value.trim() || realOf.has(value)) return value;
  if (category === 'email') {
    // Only the addresses in it: a list stays a list, a date under `to` stays a date.
    return value.replace(EMAIL_PATTERN, (address) =>
      realOf.has(address) ? address : maskEmail(address)
    );
  }
  return remember(category, value, candidateFor(category, value.trim()));
}

// ── Walking a response ──────────────────────────────────────────────────────

interface JsonObject {
  [key: string]: unknown;
}

const isPlainObject = (value: unknown): value is JsonObject => {
  if (value === null || typeof value !== 'object') return false;
  const proto: unknown = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};

/** Containers this module produced: handing one in again gives it back as it is. */
const maskedContainers = new WeakSet<object>();

/** A string field of an object: its own category, else (generic fields) the holder's. */
function maskField(key: string, value: string, holder: MaskContext): string {
  const k = normaliseKey(key);
  const own = categoryOfKey(k);
  if (own) return maskString(value, own);
  if (GENERIC_KEYS.has(k))
    return maskString(value, holder.generic ?? (k === 'name' ? 'route' : null));
  return value;
}

/** Mask a container (or a string item of a list, by the list's context). */
function maskNode(node: unknown, context: MaskContext): unknown {
  if (typeof node === 'string') return maskString(node, context.items);
  if (Array.isArray(node)) {
    if (maskedContainers.has(node)) return node;
    const out = node.map((item) => maskNode(item, context));
    maskedContainers.add(out);
    return out;
  }
  if (isPlainObject(node)) {
    if (maskedContainers.has(node)) return node;
    const out: JsonObject = {};
    for (const [key, value] of Object.entries(node)) {
      if (typeof value === 'string') out[key] = maskField(key, value, context);
      else if (Array.isArray(value) || isPlainObject(value))
        out[key] = maskNode(value, contextOf(key, context));
      else out[key] = value;
    }
    maskedContainers.add(out);
    return out;
  }
  // Numbers, booleans, null, Blob, ArrayBuffer, Date, FormData, …: as they are.
  return node;
}

/**
 * A response body with every sensitive value masked (a copy; the input is not changed). Does
 * nothing outside demo mode, and gives an already-masked body back as it is. `url` (the request
 * URL) gives a bare list of names, or a generic `name`, its meaning.
 */
export function maskResponseData<T = unknown>(data: T, url?: string): T {
  if (!_isDemoMode) return data;
  return maskNode(data, contextOfUrl(url)) as T;
}

/** Downloads are handed over untouched (a Blob used to become `{}`). */
const BINARY_RESPONSE_TYPES = new Set(['blob', 'arraybuffer', 'stream', 'document']);

/** The response interceptor: masks `response.data` in place (demo mode only). */
export function maskAxiosResponse<R extends AxiosResponse>(response: R): R {
  if (!_isDemoMode || !response) return response;
  const responseType = response.config?.responseType;
  if (responseType && BINARY_RESPONSE_TYPES.has(responseType)) return response;
  response.data = maskResponseData(response.data, response.config?.url);
  return response;
}

// ── Requests: masked values back to the real ones ──────────────────────────

const realValue = (value: string): string => realOf.get(value) ?? value;

function unmaskDeep(node: unknown): unknown {
  if (typeof node === 'string') {
    if (realOf.has(node)) return realOf.get(node);
    // A list of masked addresses sent back as one string.
    return node.includes('@demo.example.com') ? node.replace(EMAIL_PATTERN, realValue) : node;
  }
  if (Array.isArray(node)) return node.map(unmaskDeep);
  if (isPlainObject(node)) {
    const out: JsonObject = {};
    for (const [key, value] of Object.entries(node)) out[key] = unmaskDeep(value);
    return out;
  }
  return node;
}

function unmaskUrl(url: string): string {
  const queryAt = url.indexOf('?');
  const path = queryAt === -1 ? url : url.slice(0, queryAt);
  const query = queryAt === -1 ? '' : url.slice(queryAt + 1);
  const newPath = path
    .split('/')
    .map((segment) => {
      let decoded: string;
      try {
        decoded = decodeURIComponent(segment);
      } catch {
        return segment;
      }
      const real = realOf.get(decoded);
      return real === undefined ? segment : encodeURIComponent(real);
    })
    .join('/');
  let newQuery = query;
  if (query) {
    const params = new URLSearchParams(query);
    let changed = false;
    for (const [name, value] of Array.from(params.entries())) {
      const real = realOf.get(value);
      if (real !== undefined) {
        params.set(name, real);
        changed = true;
      }
    }
    if (changed) newQuery = params.toString();
  }
  return newQuery ? `${newPath}?${newQuery}` : newPath;
}

/** Requests already put back (a request retried after a 401 runs the interceptors again). */
const unmaskedRequests = new WeakSet<object>();

/**
 * The request interceptor: a masked value the page sends (a path segment, a query parameter,
 * a field of a JSON body) goes out as the real value it stands for, so drill-downs and filters
 * work and a form saved in demo mode does not write made-up values into real records.
 */
export function unmaskRequestConfig<C extends InternalAxiosRequestConfig>(config: C): C {
  if (!_isDemoMode || realOf.size === 0 || !config || unmaskedRequests.has(config)) return config;
  unmaskedRequests.add(config);
  if (typeof config.url === 'string') config.url = unmaskUrl(config.url);
  if (isPlainObject(config.params)) config.params = unmaskDeep(config.params);
  if (Array.isArray(config.data) || isPlainObject(config.data))
    config.data = unmaskDeep(config.data);
  return config;
}

/** Mask this client's responses and unmask its requests (both only in demo mode). */
export function installDemoMasking<I extends AxiosInstance>(instance: I): I {
  instance.interceptors.request.use(unmaskRequestConfig);
  instance.interceptors.response.use(maskAxiosResponse);
  return instance;
}

/** Forget every mask (demo mode switched off). */
export function resetMasks(): void {
  maskOf.clear();
  realOf.clear();
}
